/**
 * Blocked reasons for the settings screens — «[why]. [what to do].».
 *
 * Local to this feature (a reason two features share goes in `lib/reasons.ts`).
 * Kept in one module rather than beside each button so `reasons.test.ts` can
 * hold every one of them to the shape, including those handed to a modal
 * footer, which the panel-wide `reason-copy` scan does not reach.
 */

/** Rooms › the room dialog. */
export const ROOM_NAME_EMPTY = 'نام اتاق رو وارد نکردید. یک نام برای اتاق بنویسید.';

/** Security › enabling two-factor sign-in. */
export const TOTP_CODE_INCOMPLETE =
  'کد شش‌رقمی کامل نیست. کد برنامهٔ رمز یک‌بارمصرف رو کامل وارد کنید.';

/** Security › a dialog gated on a live code (or a recovery code). */
export const SECURITY_CODE_INCOMPLETE =
  'کد هنوز کامل نیست. کد برنامهٔ رمز یک‌بارمصرف یا یکی از کدهای بازیابی رو کامل وارد کنید.';

/** Security › recovery codes, shown once. */
export const RECOVERY_CODES_NOT_CONFIRMED =
  'هنوز ذخیره کردن کدها رو تأیید نکردید. کدها رو کپی یا دانلود کنید و تیک پایین رو بزنید.';

/** Starter kit › the viewer lacks one of the two permissions a kit needs. */
export const STARTER_KIT_NO_PERMISSION =
  'برای استفاده از بستهٔ راه‌اندازی، دسترسی «ویرایش تنظیمات» و «مدیریت خدمات و تعرفه» لازم است. از مالک کلینیک بخواید بسته رو اجرا کنه.';

/** Starter kit › nothing in the kit is new for this clinic. */
export const STARTER_KIT_NOTHING_NEW =
  'همهٔ موارد این بسته رو از قبل دارید. نیازی به اجرای دوباره نیست.';

/**
 * Starter kit › the preview failed to load. (While it is still loading the
 * button is `loading` — work in flight is never a reason.)
 */
export const STARTER_KIT_PREVIEW_FAILED =
  'پیش‌نمایش بسته بارگذاری نشد. «تلاش دوباره» رو در همین پنجره بزنید.';

/**
 * Starter kit › the server refused the kit on the grant ceiling: it would
 * create roles holding keys the viewer does not hold. Pressing again cannot
 * change that; the Alert in the dialog names the keys.
 */
export const STARTER_KIT_GRANT_REFUSED =
  'بعضی نقش‌های این بسته، دسترسی‌هایی دارن که حساب شما نداره. از مالک کلینیک بخواید بسته رو اجرا کنه.';

/** Location picker › manual entry › «ثبت مختصات» with nothing typed. */
export const LOCATION_EMPTY =
  'هنوز مختصاتی وارد نشده است. عددهای مختصات یا لینک نقشه رو در کادر کنار آن وارد کنید.';

/** Location picker › manual entry › «ثبت مختصات» with text that is not a location. */
export const LOCATION_UNREADABLE =
  'در این متن مختصاتی پیدا نشد. عرض و طول جغرافیایی رو از نقشه کپی کنید.';

/** Patient import › the dry run with no file chosen. */
export const IMPORT_NO_FILE =
  'هنوز فایلی انتخاب نشده است. فایل فهرست بیماران رو در کادر بالا بگذارید.';

/** Verification › «ارسال برای بررسی». */
export const VERIFICATION_NO_LICENSE_NO =
  'شمارهٔ پروانه هنوز وارد نشده است. اون رو در کادر بالا بنویسید.';
export const VERIFICATION_NO_FILES =
  'هنوز مدرکی بارگذاری نشده است. فایل پروانه رو در کادر بالا بگذارید.';
export const VERIFICATION_NO_LICENSE_FILE =
  'هیچ فایلی از نوع «پروانهٔ پزشکی» نیست. نوع یکی از فایل‌ها رو «پروانهٔ پزشکی» کنید.';

/* ── Billing (pricing build: subscription, «نتایج و صورت‌حساب», SMS credit) ── */

/** Wallet top-up › nothing typed. The bounds are the line under the button, as `Money`. */
export const TOPUP_EMPTY = 'مبلغ شارژ نوشته نشده است. مبلغی در بازهٔ زیر بنویسید.';

/** Wallet top-up › outside the server's own bounds (`WalletResponse.min/maxTopUpRials`). */
export const TOPUP_OUT_OF_RANGE = 'مبلغ شارژ بیرون از بازهٔ مجاز است. مبلغی در بازهٔ زیر بنویسید.';

/** Trial › «امتحان پلن …» while a change waits for the period's end (`SUBSCRIPTION_CHANGE_SCHEDULED`). */
export const TRIAL_SWITCH_SCHEDULED =
  'یک تغییر پلن برای پایان دوره ثبت شده. اول همون تغییر رو از بالای صفحه لغو کنید.';

/** Results › «اعتراض» on a row whose window has closed (`disputeBlock: WINDOW_CLOSED`). */
export const DISPUTE_WINDOW_CLOSED =
  'مهلت اعتراض به این ماه تمام شده است. برای پیگیری با پشتیبانی بوکورا تماس بگیرید.';

/** Results › a row that already carries a dispute (`ALREADY_DISPUTED`). */
export const DISPUTE_ALREADY_RAISED =
  'برای این ردیف اعتراض ثبت شده است. نتیجه در همین جدول نشان داده می‌شه.';

/** Results › an included / trial / capped row (`NOT_CHARGEABLE`). */
export const DISPUTE_NOT_CHARGEABLE =
  'این ردیف هزینه‌ای نداشته است. فقط ردیف‌های دارای هزینه قابل اعتراض‌اند.';

/** Results › a voided row (`VOIDED`). */
export const DISPUTE_VOIDED = 'این ردیف باطل شده و هزینه‌ای ندارد. کاری لازم نیست.';

/** Dispute modal › no reason picked. */
export const DISPUTE_REASON_MISSING =
  'دلیل اعتراض انتخاب نشده است. یکی از گزینه‌های بالا رو انتخاب کنید.';

/** Dispute modal › «دلیل دیگر» with no note. */
export const DISPUTE_NOTE_MISSING = 'توضیح اعتراض نوشته نشده است. دلیل رو در یک جمله بنویسید.';

/** Alert budget › «ذخیره» with no amount. */
export const ALERT_BUDGET_EMPTY =
  'مبلغ هشدار نوشته نشده است. مبلغی بنویسید یا «حذف هشدار» رو بزنید.';

/** Alert budget › «ذخیره» with the amount already saved. */
export const ALERT_BUDGET_UNCHANGED =
  'این مبلغ همین حالا ذخیره است. برای تغییر، مبلغ دیگری بنویسید.';

/** Refund › no IBAN typed yet. */
export const REFUND_IBAN_MISSING = 'شمارهٔ شبا رو کامل وارد کنید؛ با «IR» در ابتدای اون.';

/** Refund › the IBAN is not a valid Iranian «شبا». */
export const REFUND_IBAN_INVALID =
  'شمارهٔ شبا درست نیست. شبای حساب رو کامل و با «IR» در ابتدا وارد کنید.';

/** Refund › no account holder. */
export const REFUND_HOLDER_MISSING =
  'نام صاحب حساب رو وارد کنید؛ دقیقاً همون‌طور که در بانک ثبت شده.';
